import { Fragment } from "react";
import { StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import { Dialog, Transition } from "@headlessui/react";
import { Link } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { formatPrice } from "shared/AppFunctions/formatPrice";

export interface PackageForStay {
  package_uuid: string | number;
  slug?: string;
  name: string;
  cover?: string;
  price?: number | string;
  currency?: string;
  duration_days?: number;
  location?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  packages: PackageForStay[];
  loading?: boolean;
  stayName?: string;
}

/**
 * Modal listing the holiday packages that include a stay flagged as
 * `only_in_package`. Used when the user wants to book a stay that is
 * only available inside a package.
 *
 * Loading skeleton: 3 placeholder cards.
 * Empty state: explanatory message + dismiss.
 * Otherwise: scrollable list of clickable cards linking to the
 * holiday package detail page.
 */
const PackagesForStayModal: React.FC<Props> = ({
  open,
  onClose,
  packages,
  loading = false,
  stayName,
}) => {
  const sorted = [...packages].sort(
    (a, b) => Number(a.price ?? 0) - Number(b.price ?? 0),
  );

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={onClose}
        aria-labelledby="packages-modal-title"
        aria-describedby="packages-modal-desc"
      >
        <div className="min-h-screen px-3 sm:px-4 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-neutral-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <span
            className="inline-block h-screen align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel
              className="inline-flex flex-col w-full text-left align-middle transition-all transform bg-white dark:bg-neutral-900 dark:text-neutral-100 rounded-2xl shadow-2xl shadow-neutral-900/10 dark:shadow-black/40 border border-neutral-200 dark:border-neutral-800 max-w-lg sm:max-w-xl my-6 sm:my-10"
              style={{ maxHeight: "calc(100vh - 4rem)" }}
            >
              {/* HEADER */}
              <header className="flex items-start justify-between gap-4 px-5 sm:px-6 pt-5 pb-4 border-b border-neutral-200 dark:border-neutral-800">
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-semibold tracking-wider uppercase text-rose-600 dark:text-rose-400">
                    Solo in pacchetto
                  </p>
                  <Dialog.Title
                    id="packages-modal-title"
                    className="mt-1 text-lg sm:text-xl font-semibold leading-snug text-neutral-900 dark:text-white"
                  >
                    Pacchetti disponibili
                  </Dialog.Title>
                  <p
                    id="packages-modal-desc"
                    className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed"
                  >
                    {stayName
                      ? `“${stayName}” è prenotabile solo all'interno di un pacchetto. Scegli un pacchetto per continuare.`
                      : "Questa struttura è prenotabile solo all'interno di un pacchetto. Scegli un pacchetto per continuare."}
                  </p>
                </div>
                <ButtonClose onClick={onClose} />
              </header>

              {/* BODY */}
              <div className="flex-1 overflow-y-auto overscroll-contain px-3 sm:px-5 py-4">
                {loading ? (
                  <SkeletonList />
                ) : sorted.length === 0 ? (
                  <EmptyState onClose={onClose} />
                ) : (
                  <ul className="space-y-2.5" role="list">
                    {sorted.map((pkg) => (
                      <li key={pkg.package_uuid}>
                        <PackageRow pkg={pkg} onSelect={onClose} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* FOOTER */}
              <footer className="flex items-center justify-between gap-3 px-5 sm:px-6 py-3 border-t border-neutral-200 dark:border-neutral-800 text-xs text-neutral-500 dark:text-neutral-400">
                <span>
                  {sorted.length > 0
                    ? `${sorted.length} pacchett${sorted.length === 1 ? "o" : "i"} disponibil${sorted.length === 1 ? "e" : "i"}`
                    : "Nessun pacchetto al momento"}
                </span>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:text-neutral-900 dark:hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 rounded px-2 py-1"
                >
                  Chiudi
                </button>
              </footer>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

interface RowProps {
  pkg: PackageForStay;
  onSelect: () => void;
}

const PackageRow: React.FC<RowProps> = ({ pkg, onSelect }) => {
  const href = stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, pkg.slug || pkg.package_uuid);
  const priceLabel = pkg.price
    ? formatPrice(
        Number(pkg.price),
        true,
        window.navigator.language,
        pkg.currency || "EUR",
      )
    : null;

  return (
    <Link
      to={href}
      onClick={onSelect}
      className="group flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/60 hover:bg-white dark:hover:bg-neutral-800 border border-transparent hover:border-rose-200 dark:hover:border-rose-500/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 transition-all duration-200 min-h-[72px]"
    >
      {pkg.cover ? (
        <img
          src={pkg.cover}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg object-cover flex-shrink-0 bg-neutral-200 dark:bg-neutral-700"
        />
      ) : (
        <div
          aria-hidden="true"
          className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg flex-shrink-0 bg-gradient-to-br from-rose-100 to-rose-200 dark:from-rose-900/40 dark:to-rose-800/30 flex items-center justify-center"
        >
          <i className="las la-suitcase-rolling text-2xl text-rose-500/70 dark:text-rose-400/70" />
        </div>
      )}

      <div className="flex-1 min-w-0">
        <p className="font-medium text-neutral-900 dark:text-white truncate">
          {pkg.name}
        </p>
        <div className="mt-0.5 flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          {pkg.duration_days != null && (
            <span className="inline-flex items-center gap-1">
              <i className="las la-calendar text-base leading-none" aria-hidden="true" />
              {pkg.duration_days} {pkg.duration_days === 1 ? "giorno" : "giorni"}
            </span>
          )}
          {pkg.duration_days != null && pkg.location && (
            <span aria-hidden="true" className="text-neutral-300 dark:text-neutral-600">·</span>
          )}
          {pkg.location && (
            <span className="truncate inline-flex items-center gap-1">
              <i className="las la-map-marker text-base leading-none" aria-hidden="true" />
              {pkg.location}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-col items-end gap-1 flex-shrink-0">
        {priceLabel && (
          <span className="text-sm font-semibold text-neutral-900 dark:text-white tabular-nums">
            {priceLabel}
          </span>
        )}
        <span
          aria-hidden="true"
          className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-neutral-700 text-neutral-400 group-hover:bg-rose-500 group-hover:text-white group-hover:translate-x-0.5 transition-all duration-200"
        >
          <i className="las la-arrow-right text-lg leading-none" />
        </span>
      </div>
    </Link>
  );
};

const SkeletonList: React.FC = () => (
  <div className="space-y-2.5" aria-hidden="true">
    {Array.from({ length: 3 }).map((_, i) => (
      <div
        key={i}
        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/60"
      >
        <Skeleton width={64} height={64} className="rounded-lg flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-2">
          <Skeleton width="70%" height={14} />
          <Skeleton width="40%" height={12} />
        </div>
        <Skeleton width={56} height={20} />
      </div>
    ))}
  </div>
);

const EmptyState: React.FC<{ onClose: () => void }> = ({ onClose }) => (
  <div className="flex flex-col items-center justify-center text-center px-4 py-10">
    <div
      aria-hidden="true"
      className="w-12 h-12 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-3"
    >
      <i className="las la-suitcase text-2xl text-neutral-400" />
    </div>
    <p className="text-sm font-medium text-neutral-900 dark:text-white">
      Nessun pacchetto al momento
    </p>
    <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400 max-w-sm">
      Al momento non ci sono pacchetti pubblicati che includono questa
      struttura. Torna a trovarci più tardi.
    </p>
    <button
      type="button"
      onClick={onClose}
      className="mt-4 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:text-neutral-900 dark:hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900 rounded px-3 py-1.5"
    >
      Chiudi
    </button>
  </div>
);

export default PackagesForStayModal;
